import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, CheckCircle, XCircle, MessageSquare, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

interface ApprovalActionsProps {
  assetId: string;
  action: 'approve' | 'reject' | 'request_docs';
  onClose: () => void;
  onSuccess: () => void;
}

const actionConfig = {
  approve: {
    title: 'Aprovar Ativo',
    description: 'Confirme a aprovação deste ativo para tokenização.',
    icon: CheckCircle,
    iconColor: 'text-green-400',
    buttonText: 'Confirmar Aprovação',
    buttonColor: 'bg-green-600 hover:bg-green-700',
    notesLabel: 'Observações da aprovação (opcional)',
    notesPlaceholder: 'Adicione observações sobre a aprovação...',
  },
  reject: {
    title: 'Rejeitar Ativo',
    description: 'Informe o motivo da rejeição deste ativo.',
    icon: XCircle,
    iconColor: 'text-red-400',
    buttonText: 'Confirmar Rejeição',
    buttonColor: 'bg-red-600 hover:bg-red-700',
    notesLabel: 'Motivo da rejeição (obrigatório)',
    notesPlaceholder: 'Descreva o motivo da rejeição...',
  },
  request_docs: {
    title: 'Solicitar Documentos',
    description: 'Solicite documentos adicionais ao emissor.',
    icon: MessageSquare,
    iconColor: 'text-yellow-400',
    buttonText: 'Enviar Solicitação',
    buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
    notesLabel: 'Documentos solicitados (obrigatório)',
    notesPlaceholder: 'Liste os documentos necessários e instruções...',
  },
};

const documentTypes = [
  { value: 'LEGAL_OPINION', label: 'Parecer Jurídico' },
  { value: 'VALUATION_REPORT', label: 'Laudo de Avaliação' },
  { value: 'CUSTODY_PROOF', label: 'Comprovante de Custódia' },
  { value: 'OWNERSHIP_PROOF', label: 'Comprovante de Propriedade' },
  { value: 'REGULATORY_APPROVAL', label: 'Aprovação Regulatória' },
  { value: 'FINANCIAL_STATEMENTS', label: 'Demonstrações Financeiras' },
  { value: 'CONTRACT', label: 'Contrato' },
  { value: 'OTHER', label: 'Outro' },
];

export function ApprovalActions({ assetId, action, onClose, onSuccess }: ApprovalActionsProps) {
  const config = actionConfig[action];
  const Icon = config.icon;
  
  const [notes, setNotes] = useState('');
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const endpoint = action === 'approve' 
        ? `/tokenization/compliance/assets/${assetId}/approve`
        : action === 'reject'
        ? `/tokenization/compliance/assets/${assetId}/reject`
        : `/tokenization/compliance/assets/${assetId}/request-documents`;

      const payload = action === 'request_docs'
        ? { notes, requestedDocuments: selectedDocs }
        : { notes };

      await api.post(endpoint, payload);
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Erro ao processar ação');
    },
  });

  const handleSubmit = () => {
    setError('');

    if (action === 'reject' && !notes.trim()) {
      setError('O motivo da rejeição é obrigatório');
      return;
    }

    if (action === 'request_docs') {
      if (selectedDocs.length === 0) {
        setError('Selecione pelo menos um tipo de documento');
        return;
      }
      if (!notes.trim()) {
        setError('As instruções são obrigatórias');
        return;
      }
    }

    mutation.mutate();
  };

  const toggleDoc = (docType: string) => {
    setSelectedDocs(prev => 
      prev.includes(docType)
        ? prev.filter(d => d !== docType)
        : [...prev, docType]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-surface-900 rounded-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-800">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              action === 'approve' ? 'bg-green-500/20' :
              action === 'reject' ? 'bg-red-500/20' :
              'bg-yellow-500/20'
            }`}>
              <Icon className={`w-5 h-5 ${config.iconColor}`} />
            </div>
            <h2 className="text-lg font-semibold">{config.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <p className="text-surface-400">{config.description}</p>

          {/* Document Selection for request_docs */}
          {action === 'request_docs' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Tipos de Documentos Solicitados
              </label>
              <div className="grid grid-cols-2 gap-2">
                {documentTypes.map((doc) => (
                  <label
                    key={doc.value}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedDocs.includes(doc.value)
                        ? 'bg-primary-600/20 border border-primary-500'
                        : 'bg-surface-800 border border-surface-700 hover:border-surface-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDocs.includes(doc.value)}
                      onChange={() => toggleDoc(doc.value)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedDocs.includes(doc.value)
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-surface-600'
                    }`}>
                      {selectedDocs.includes(doc.value) && (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </div>
                    <span className="text-sm">{doc.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {config.notesLabel}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={config.notesPlaceholder}
              className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg"
              rows={4}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-surface-800">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${config.buttonColor}`}
          >
            {mutation.isPending ? 'Processando...' : config.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ApprovalActions;
